import React from 'react';

import { ICellRendererFunc, ICellRendererParams } from 'ag-grid-community';
import { BzDateTime } from '@benzinga/date-utils';

import { formatPrice, numberShorthand, numberWithCommas } from '../../../utils/formatters';
import { NO_VALUE } from '../../../utils/global';
import { TickerColor } from '@benzinga/pro-ui';
import { StockSymbol } from '@benzinga/session';

export type Cell = any; // TODO
export type CellRendererFramework = any;
type ICellRendererParamsWithStore = ICellRendererParams & { store: any };

export type HigherOrderify<T> = (...args: any[]) => T;

export interface CellClassRules {
  [name: string]: {
    [cssClassName: string]: (cell: Cell) => boolean;
  };
}

export const cellClassRules: CellClassRules = {
  upDownText: {
    'u-downText': cell => parseFloat(cell.value) < 0,
    'u-upText': cell => parseFloat(cell.value) > 0,
  },
};

const formatPriceHOF: HigherOrderify<ICellRendererFunc> = (beforeValue: string, afterValue: string) => {
  const formatter: ICellRendererFunc = (data: ICellRendererParams) => {
    if (data.value) {
      return `${beforeValue || ''}${formatPrice(data.value)}${afterValue || ''}`;
    }

    return NO_VALUE;
  };
  return formatter;
};

const priceFormatter: ICellRendererFunc = (data: ICellRendererParams) => {
  if (data.value) {
    return formatPrice(data.value);
  }
  return NO_VALUE;
};

const precisionRound = (num: number, precision: number) => {
  const factor = Math.pow(10, precision);
  return Math.round(num * factor) / factor;
};

export const cellRenderer = {
  computePercent: data => {
    const num = Number(data.value);
    if (!Number.isFinite(num) || num === 0) {
      return NO_VALUE;
    }
    return `${precisionRound(num * 100, 2)}%`;
  },

  formatPrice: priceFormatter,

  formatPriceHOF,

  numberShorthand: data => numberShorthand(data.value) || NO_VALUE,

  numberWithCommas: data => numberWithCommas(data.value) || NO_VALUE,

  roundPercent: data => {
    const rounded = Math.round(data.value * 100) / 100;
    return rounded ? `${rounded.toLocaleString()}%` : '';
  },

  timeHOF: () => data => {
    const time = BzDateTime.fromTime(data.value).toISOTimeString({ timeFormat: 'AM/PM' });
    return time === 'Invalid date' ? data.value : time;
  },
};

export interface CellRendererFrameworks {
  [name: string]: CellRendererFramework | HigherOrderify<CellRendererFramework>;
}

export const cellRendererFramework: CellRendererFrameworks = {
  symbol: (onSymbolClick: (symbol: StockSymbol) => void) => {
    return React.forwardRef<HTMLElement, ICellRendererParamsWithStore>((data, ref) => {
      if (data.value) {
        return (
          <span ref={ref}>
            <TickerColor onSymbolClick={onSymbolClick} symbol={data.value} />
          </span>
        );
      }
      return null;
    });
  },
};

export const gridOptions = {
  sideBar: {
    toolPanels: [
      {
        iconKey: 'columns',
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressPivotMode: true,
          suppressPivots: true,
          suppressRowGroups: true,
          suppressValues: true,
        },
      },
      {
        iconKey: 'filter',
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        toolPanel: 'agFiltersToolPanel',
      },
    ],
  },
  statusBar: {
    statusPanels: [
      {
        statusPanel: 'agAggregationComponent',
      },
    ],
  },
};

export const columnDefs = {
  symbol: {
    cellClass: ['u-alignLeft', 'u-cursor--pointer'],
    cellRendererFramework: cellRendererFramework.symbol,
    cellStyle: { textAlign: 'left' },
    field: 'symbol',
    filter: 'set',
    headerName: 'Symbol',
    pinned: 'left',
  },
};
